import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { authAPI } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { InputOTP, InputOTPGroup, InputOTPSlot } from '../components/ui/input-otp';
import { toast } from 'sonner';
import { Mail, Phone, Loader2, ArrowLeft, Eye, EyeOff } from 'lucide-react';

const LOGO_URL = process.env.REACT_APP_LOGO_URL;

export default function Login() {
  const navigate = useNavigate();
  const { login } = useAuth();
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('email');
  
  // Email login state
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  
  // Phone login state
  const [phone, setPhone] = useState('');
  const [phonePassword, setPhonePassword] = useState('');
  const [showOTP, setShowOTP] = useState(false);
  const [otp, setOtp] = useState('');
  const [mockOTP, setMockOTP] = useState('');

  const handleEmailLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      const userData = await login({ email, password });
      toast.success('Erfolgreich angemeldet!');
      // Redirect based on role
      if (userData.role === 'admin') {
        navigate('/admin');
      } else if (userData.role === 'field_owner') {
        navigate('/owner');
      } else {
        navigate('/dashboard');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Anmeldung fehlgeschlagen');
    } finally {
      setLoading(false);
    }
  };

  const handlePhoneLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      await login({ phone, password: phonePassword });
      toast.success('Erfolgreich angemeldet!');
      navigate('/dashboard');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Anmeldung fehlgeschlagen');
    } finally {
      setLoading(false);
    }
  };

  const handleRequestOTP = async () => {
    if (!phone) {
      toast.error('Bitte gib deine Telefonnummer ein');
      return;
    }
    
    setLoading(true);
    try {
      const response = await authAPI.requestOTP(phone);
      setMockOTP(response.data.mock_otp);
      setShowOTP(true);
      toast.success(`OTP gesendet! (Mock: ${response.data.mock_otp})`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'OTP-Anfrage fehlgeschlagen');
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyOTP = async () => {
    if (otp.length !== 6) {
      toast.error('Bitte gib den vollständigen OTP ein');
      return;
    }
    
    setLoading(true);
    try {
      // Use OTP login endpoint - creates account if not exists
      const response = await authAPI.otpLogin(phone, otp);
      
      // Store token and user data
      localStorage.setItem('token', response.data.token);
      localStorage.setItem('user', JSON.stringify(response.data.user));
      
      toast.success('Erfolgreich angemeldet!');
      
      // Redirect based on role
      const userRole = response.data.user.role;
      if (userRole === 'admin') {
        navigate('/admin');
      } else if (userRole === 'field_owner') {
        navigate('/owner');
      } else {
        navigate('/dashboard');
      }
      window.location.reload(); // Refresh to update auth state
    } catch (error) {
      toast.error(error.response?.data?.detail || 'OTP-Verifizierung fehlgeschlagen');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background flex flex-col items-center justify-center p-4 hex-pattern">
      <Link to="/" className="absolute top-6 left-6 flex items-center gap-2 text-tunis-blue hover:text-tunis-teal transition-colors">
        <ArrowLeft className="w-5 h-5" />
        Zurück
      </Link>
      
      <div className="w-full max-w-md space-y-6 animate-fade-in">
        {/* Logo */}
        <div className="text-center">
          <Link to="/" className="inline-block">
            <img src={LOGO_URL} alt="Tunis Kick" className="h-20 w-20 mx-auto rounded-full shadow-lg" />
          </Link>
          <h1 className="mt-4 font-display font-bold text-2xl text-tunis-blue">Willkommen zurück!</h1>
          <p className="text-muted-foreground">Melde dich an, um fortzufahren</p>
        </div>

        <Card className="border-slate-200 shadow-card">
          <CardHeader className="pb-4">
            <CardTitle className="text-lg">Anmelden</CardTitle>
            <CardDescription>Wähle deine bevorzugte Anmeldemethode</CardDescription>
          </CardHeader>
          <CardContent>
            <Tabs value={activeTab} onValueChange={setActiveTab}>
              <TabsList className="grid w-full grid-cols-2 mb-6">
                <TabsTrigger value="email" className="flex items-center gap-2" data-testid="tab-email">
                  <Mail className="w-4 h-4" />
                  E-Mail
                </TabsTrigger>
                <TabsTrigger value="phone" className="flex items-center gap-2" data-testid="tab-phone">
                  <Phone className="w-4 h-4" />
                  Telefon
                </TabsTrigger>
              </TabsList>

              {/* Email Login */}
              <TabsContent value="email">
                <form onSubmit={handleEmailLogin} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="email">E-Mail</Label>
                    <Input
                      id="email"
                      type="email"
                      placeholder="deine@email.com"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      required
                      data-testid="input-email"
                      className="h-12"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="password">Passwort</Label>
                    <div className="relative">
                      <Input
                        id="password"
                        type={showPassword ? "text" : "password"}
                        placeholder="••••••••"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        required
                        data-testid="input-password"
                        className="h-12 pr-12"
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 transition-colors"
                        tabIndex={-1}
                      >
                        {showPassword ? (
                          <EyeOff className="w-5 h-5" />
                        ) : (
                          <Eye className="w-5 h-5" />
                        )}
                      </button>
                    </div>
                  </div>
                  <div className="flex justify-end">
                    <Link to="/forgot-password" className="text-sm text-tunis-teal hover:underline" data-testid="link-forgot-password">
                      Passwort vergessen?
                    </Link>
                  </div>
                  <Button
                    type="submit"
                    className="w-full btn-primary h-12"
                    disabled={loading}
                    data-testid="btn-login-email"
                  >
                    {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : 'Anmelden'}
                  </Button>
                </form>
              </TabsContent>

              {/* Phone Login */}
              <TabsContent value="phone">
                {!showOTP ? (
                  <div className="space-y-4">
                    <div className="space-y-2">
                      <Label htmlFor="phone">Telefonnummer</Label>
                      <Input
                        id="phone"
                        type="tel"
                        placeholder="+216 XX XXX XXX"
                        value={phone}
                        onChange={(e) => setPhone(e.target.value)}
                        data-testid="input-phone"
                        className="h-12"
                      />
                    </div>
                    <p className="text-sm text-muted-foreground">
                      Wir senden dir einen 6-stelligen Code per SMS zur Anmeldung.
                    </p>
                    <Button
                      className="w-full btn-primary h-12"
                      onClick={handleRequestOTP}
                      disabled={loading || !phone}
                      data-testid="btn-request-otp"
                    >
                      {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : 'Code anfordern'}
                    </Button>
                  </div>
                ) : (
                  <div className="space-y-4">
                    <p className="text-sm text-muted-foreground text-center">
                      Gib den 6-stelligen Code ein, der an {phone} gesendet wurde
                    </p>
                    {mockOTP && (
                      <div className="text-center p-3 bg-tunis-teal/10 rounded-lg">
                        <p className="text-sm font-medium text-tunis-teal">Mock OTP: {mockOTP}</p>
                      </div>
                    )}
                    <div className="flex justify-center">
                      <InputOTP maxLength={6} value={otp} onChange={setOtp} data-testid="input-otp">
                        <InputOTPGroup>
                          <InputOTPSlot index={0} />
                          <InputOTPSlot index={1} />
                          <InputOTPSlot index={2} />
                          <InputOTPSlot index={3} />
                          <InputOTPSlot index={4} />
                          <InputOTPSlot index={5} />
                        </InputOTPGroup>
                      </InputOTP>
                    </div>
                    <Button
                      className="w-full btn-primary h-12"
                      onClick={handleVerifyOTP}
                      disabled={loading || otp.length !== 6}
                      data-testid="btn-verify-otp"
                    >
                      {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : 'Anmelden'}
                    </Button>
                    <Button
                      variant="ghost"
                      className="w-full"
                      onClick={() => {
                        setShowOTP(false);
                        setOtp('');
                        setMockOTP('');
                      }}
                      data-testid="btn-back-phone"
                    >
                      Zurück
                    </Button>
                  </div>
                )}
              </TabsContent>
            </Tabs>
          </CardContent>
        </Card>

        <p className="text-center text-sm text-muted-foreground">
          Noch kein Konto?{' '}
          <Link to="/register" className="font-semibold text-tunis-teal hover:underline" data-testid="link-register">
            Jetzt registrieren
          </Link>
        </p>
      </div>
    </div>
  );
}
